import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import _ from 'lodash';
import './housekeeping.scss'
import { HousekeepingState, FacilityState, FacilityData, TenantState, TenantData, StaffScheduleState } from '../../../store/models/domain'
import { authProvider } from '../../../common/auth/AuthProvider'
import * as hkReducer from './reducer'
import Dashboard from './Dashboard'
import CheckList from "./CheckList"
import asyncTaskWatcher from '../../../common/asyncTaskWatcher';
import log from '../../../common/logging';
import hubAppEvents from '../../../common/hubAppEvents';
import { createSelector } from '@reduxjs/toolkit';


type StoreState = {
    housekeeping: HousekeepingState
    facilities: FacilityState
    tenants: TenantState
    staffSchedules: StaffScheduleState
}

const emptyFacility: FacilityData = { id: '', name: '', }
const emptyTenant: TenantData = { tenant: '', name: '', }

const getFacilityId = () => authProvider.getAccountInfo()?.facility?.id
const getStaffRefId = () => authProvider.getAccountInfo()?.staffMember?.staffRefId

// Memoize selector to avoid creation of new array everytime the mapstate is ran evern though no changes have made
const selectHkReports = createSelector([(s: StoreState) => s.housekeeping.reports, getFacilityId], (reports, facilityId) =>
    _.sortBy(Object.values(reports).filter(r => r.facility?.id === facilityId), r => r.reportDate)
)

const selectMarkingReports = createSelector([(s: StoreState) => s.housekeeping.auditMarkingReports, getFacilityId],
  (reports, facilityId) => _.sortBy(reports.filter(r => r.facility?.id === facilityId), r => r.reportDate)
)

const selectStaffSchedules = createSelector([(s: StoreState) => s.staffSchedules.schedules, getFacilityId, getStaffRefId],
    (schedules, facilityId, staffRefId) => schedules.filter(r => r.facility?.id === facilityId && r.staffMember?.staffRefId === staffRefId)
)

const selectCleans = createSelector([(s: StoreState) => s.housekeeping.cleans, getFacilityId],
    (cleans, facilityId) => cleans.filter(c => c.facility?.id === facilityId)
)

const mapState = (state: StoreState) => ({
    hkConfig: state.housekeeping.config,
    facility: state.facilities.facilities.find(r => r.id === getFacilityId()) ?? emptyFacility,
    tenant: state.tenants.records.find(r => r.tenant === authProvider.getAccountInfo()?.tenantId) ?? emptyTenant,
    hkReports: selectHkReports(state),
    markingReports: selectMarkingReports(state),
    staffSchedules: selectStaffSchedules(state),
    cleans: selectCleans(state),
    filteredRoomIds: state.facilities.filteredRoomIds,
})

const mapDispatch = {
}

interface Props extends PropsFromRedux, WithTranslation {
    history: any,
    match: any
}

const HousekeepingApp = (props: Props) => {

    const {
        //Translation
        t,
        i18n
    } = props;     

    const [state, dispatch] = React.useReducer(hkReducer.default, hkReducer.initialState)      
    const [isReady, setisReady] = React.useState(false);

    React.useEffect(() => {
      let cancelled = false;

      (async () => {
        try {
          // ensures the underlying connection exists and is connected (or already in progress)
          // before we try to attach handlers to it
          await asyncTaskWatcher.ensureConnected();
          
            if (cancelled) return;
            hubAppEvents.registerAppEvents({
              updateHousekeepingReport: hubAppEvents.updateHousekeepingReport
            });
          } catch (err) {
            log.error("Failed to check connection", err);
          }
      })();

      return () => {
        cancelled = true;
        hubAppEvents.unregisterAppEvents();
      };
    }, [asyncTaskWatcher, hubAppEvents])
    
    React.useEffect(() => {

      if (!props.facility?.id || props.facility === emptyFacility) return
      if (!props.tenant?.name || props.tenant === emptyTenant) return

      // only dispatch to load data when the tenant and facility already done loading
      dispatch(hkReducer.loadData(props.facility, props.hkConfig, props.tenant, props.staffSchedules, props.hkReports, props.cleans, props.markingReports)) 
      setisReady(true)
    }, [props.facility, props.hkConfig, props.tenant, props.staffSchedules, props.hkReports, props.cleans, props.markingReports])

    return (
        <div className="app">
            {isReady && (
                    state.cleaningRecord
                    ? <CheckList state={state} dispatch={dispatch} t={t} i18n={i18n} />
                    : <Dashboard state={state} dispatch={dispatch} t={t} />
            )}
            
        </div>
    );
}

const connector = connect(mapState, mapDispatch)
const translate = withTranslation()

type PropsFromRedux = ConnectedProps<typeof connector>
export default withRouter(translate(connector(HousekeepingApp)));
